// src/utils/axiosInstance.js

import axios from "axios";
import { authReadyPromise } from "@auth/AuthContext";
import { clientContextHeaders } from "./clientContext";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL, // MUST be http://localhost:8000/api/v1
  // LWI-1040: send/receive cookies on cross-origin API calls so the httpOnly
  // remember_2fa cookie round-trips (requires CORS supports_credentials=true,
  // which the API sets). Bearer-token auth is unaffected.
  withCredentials: true,
});

// LWI-582: token preference is sessionStorage > localStorage. The admin's
// session lives in localStorage; an impersonation session lives in
// sessionStorage so it's per-tab and dies when the tab closes. Reading
// sessionStorage first means impersonation requests use the impersonation
// bearer; non-impersonation requests fall through to the admin bearer.
function readCurrentToken() {
  return sessionStorage.getItem("token") ?? localStorage.getItem("token");
}

// LWI-1717 — true when the request resolves to the API's own origin.
// axios lets an absolute (or protocol-relative) URL override baseURL, so a
// caller passing a content-controlled URL (e.g. a KB image src) would
// otherwise hand the reader's bearer token and tenant to whatever host that
// URL names. Relative URLs always resolve to the API origin.
export function isApiOriginRequest(config) {
  try {
    const apiOrigin = new URL(api.defaults.baseURL ?? "", window.location.href).origin;
    // params don't affect the origin; leaving them out means a params
    // serialization error can't make an API request look foreign.
    const url = api.getUri({ ...config, params: undefined });
    return new URL(url, window.location.href).origin === apiOrigin;
  } catch {
    return false;
  }
}

// attach headers
api.interceptors.request.use(async (config) => {
  // Wait for auth to be ready before sending any request
  await authReadyPromise;
  // LWI-1717 — never send the bearer token or X-Tenant to a foreign origin.
  // AuthContext also sets a default Authorization header, which axios has
  // already merged into config.headers, so strip it here too.
  if (!isApiOriginRequest(config)) {
    config.headers.delete("Authorization");
    config.headers.delete("X-Tenant");
    return config;
  }
  const token = readCurrentToken();
  const tenant = localStorage.getItem("tenant");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // LWI-582: stamp the request with the bearer token used at send time so
    // the response interceptor can distinguish a "real" 401 (current token)
    // from a stale-post-swap 401 (request was already on the wire when
    // applyImpersonationSession / exitImpersonation flipped the active
    // bearer). Without this, exiting an impersonation while a slow request
    // is in flight would 401 the response with the now-stale impersonation
    // token, the unauthorized handler would clear admin's localStorage, and
    // the admin would be unexpectedly bumped to /login.
    config.metadata = { ...(config.metadata || {}), tokenUsed: token };
  }
  if (tenant) config.headers["X-Tenant"] = tenant;
  // LWI-1721 — informational screen/route/action context for the Audit Log.
  Object.entries(clientContextHeaders(config.method)).forEach(([k, v]) => {
    config.headers[k] = v;
  });
  return config;
});

// Registered by AuthContext to clear auth state when a 401 is received
let onUnauthorized = null;
export function registerUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

// Laravel debug-mode error bodies include server-internal fields (absolute
// file paths, stack frames, exception class). We strip these before the
// error reaches callers so they can't be accidentally logged to console
// and shipped to Datadog RUM.
const SENSITIVE_ERROR_FIELDS = ["file", "line", "trace", "exception"];

// Force logout on 401 — only fires when the user actually has a token
// (avoids triggering on unauthenticated endpoints like /auth/login).
//
// LWI-582: skip the unauthorized handler when the bearer used by the
// failing request is no longer the current bearer (i.e., the request is
// a stale-post-swap leftover). Untagged requests (no metadata.tokenUsed)
// fall through to legacy behavior — invoke the handler.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = error.response?.data;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      for (const key of SENSITIVE_ERROR_FIELDS) delete data[key];
    }

    // LWI-1717 — a 401 from a foreign origin says nothing about our session.
    if (
      error.response?.status === 401 &&
      onUnauthorized &&
      readCurrentToken() &&
      isApiOriginRequest(error.config ?? {})
    ) {
      const tokenUsed = error.config?.metadata?.tokenUsed;
      const currentToken = readCurrentToken();
      // Skip ONLY when explicitly stale: tagged AND mismatched.
      // Untagged (undefined tokenUsed) falls through to invoke handler.
      if (!tokenUsed || tokenUsed === currentToken) {
        onUnauthorized();
      }
    }
    return Promise.reject(error);
  }
);

export default api;
